/**
 * MultiDrilldownModal — one investigation surface holding SEVERAL record sets.
 *
 * DrilldownModal answers "which records are behind this number?". Some
 * drill-downs are not one number: clicking a high-risk location has to show the
 * incidents there, the open hazards there, and the risk assessments covering
 * it, because the question ("why is this location risky, and what is being done
 * about it?") spans all three. Tabs keep that in one place instead of making the
 * reader open three modals and hold the comparison in their head.
 *
 * Each tab is a plain DrilldownGrid, so every tab keeps the paging, sorting,
 * search, column chooser, CSV export and row → source-record navigation the
 * single-metric drill already has.
 *
 * `tabs`: [{ id, label, metric, extraDimensions?, timeOverride?, note? }]
 */
import React, { useState } from 'react';
import { X } from 'lucide-react';
import DrilldownGrid from './DrilldownGrid';

export default function MultiDrilldownModal({
  open, title, subtitle = null, tabs = [], onClose,
}) {
  const [active, setActive] = useState(0);
  if (!open || tabs.length === 0) return null;
  const current = tabs[Math.min(active, tabs.length - 1)];

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-5xl rounded-lg bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">{title || 'Records'}</h2>
            {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {tabs.length > 1 && (
          <div className="mb-4 flex flex-wrap gap-1 border-b border-gray-200" role="tablist">
            {tabs.map((tab, i) => {
              const on = i === Math.min(active, tabs.length - 1);
              return (
                <button
                  key={tab.id || i}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => setActive(i)}
                  className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                    on
                      ? 'border-blue-600 text-blue-700'
                      : 'border-transparent text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}

        {current.note && (
          <p className="mb-3 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">{current.note}</p>
        )}

        {/* Keyed so switching tabs starts a clean grid (page 1, no stale sort). */}
        <DrilldownGrid
          key={current.id || active}
          metric={current.metric}
          title={`${title || ''} — ${current.label}`}
          extraDimensions={current.extraDimensions || null}
          timeOverride={current.timeOverride || null}
        />
      </div>
    </div>
  );
}
